import React, {
  useState,
  useRef,
  ReactElement,
  cloneElement,
  useEffect,
} from "react";
import { Popper, Paper, Box, Typography, Theme, useTheme } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import OnOffToggle from "./common/OnOffToggle";
import { useIntl } from "react-intl";
import ButtonComponent from "./common/ButtonComponent";
import {
  setPreviewMode,
  setPreviewOptions,
  loadRemainingProducts,
  setRemainingProductsModal,
} from "store/product-list/ProductListActions";
import { useDispatch, useSelector } from "react-redux";
import { selectPreviewOptions } from "store/product-list/ProductListSelectors";
import { formatDateForAnalytics } from "utils/TimeUtils";
import { selectProducts } from "store/product/ProductSelectors";
import {
  selectIsPreviewMode,
  selectRemainingProductsModal,
  selectProductIds,
  selectContentSlots,
} from "store/product-list/ProductListSelectors";
import { selectCurrentCategory } from "store/category/CategorySelectors";
import { selectCurrentCatalogId } from "store/catalog/CatalogSelectors";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "store/store-list/StoreListSelectors";
import { selectIsCategoryProductsV2Available } from "store/adapter-config/AdapterConfigSelectors";
import AppEnv from "utils/AppEnv";

interface PreviewPopupProps {
  children: ReactElement;
}

const useStyles = (theme: Theme) => {
  return {
    description: {
      padding: "10px 0px 10px 0px",
      fontSize: "14px",
      fontWeight: 400,
      fontFamily: "Helvetica",
      color: theme.palette.text.primary,
    },
    dailogAction: {
      width: "100%",
      marginTop: "20px",
      display: "flex",
      justifyContent: "space-between",
    },
    toggleDiv: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "10px",
    },
    paper: {
      padding: theme.spacing(2),
      width: 220,
      paddingTop: 20,
    },
    textField: {
      marginBottom: theme.spacing(2),
    },
    datePicker: {
      width: "100%",
      marginBottom: theme.spacing(2),
      "& .MuiFormLabel-root": {
        fontSize: "14px",
        fontWeight: 400,
        fontFamily: "Helvetica",
      },
      "& .MuiFormLabel-root.Mui-focused": {
        color: theme.palette.focusColor?.main || theme.palette.primary.main,
      },
      "& .MuiInputBase-root.Mui-focused fieldset": {
        borderColor:
          theme.palette.focusColor?.main || theme.palette.primary.main,
      },
      "& .MuiInputBase-root input": {
        fontSize: "14px",
        fontWeight: 400,
        fontFamily: "Helvetica",
      },
    },
    datePickerPopper: {
      zIndex: 1400,
    },
    backdrop: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1299,
      backgroundColor: "transparent",
    },
    popper: {
      zIndex: 1300,
    },
    popperWithModal: {
      zIndex: 0, // Lower z-index when loading modal is open
    },
  };
};

export default function PreviewPopup({ children }: PreviewPopupProps) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const products = useSelector(selectProducts);
  const remainingProductsModal = useSelector(selectRemainingProductsModal);
  const allProductIds = useSelector(selectProductIds);
  const contentSlots = useSelector(selectContentSlots);
  const categoryId = useSelector(selectCurrentCategory);
  const catalogId = useSelector(selectCurrentCatalogId);
  const localeCode = useSelector(selectCurrentLocale);
  const storeId = useSelector(selectCurrentStoreId);
  const isCategoryProductsV2Available = useSelector(
    selectIsCategoryProductsV2Available,
  );
  const [open, setOpen] = useState(false);
  const prevModalOpen = useRef(remainingProductsModal.isOpen);
  const prevIsCancelled = useRef(remainingProductsModal.isCancelled);
  const previewOptions = useSelector(selectPreviewOptions);
  const isPreviewMode = useSelector(selectIsPreviewMode);
  const [date, setDate] = useState<Dayjs | null>(null);
  const [stock, setStock] = useState<"hide" | "show">("show");
  const [published, setPublished] = useState<"hide" | "show">("show");
  const theme = useTheme();
  const classes = useStyles(theme);
  const anchorRef = useRef<HTMLElement>(null);
  const pendingPreviewOptions = useRef<{
    date: Dayjs | null;
    stock: "hide" | "show";
    published: "hide" | "show";
  } | null>(null);
  const isWaitingForProducts = useRef(false);

  // Handle remaining products modal state changes
  useEffect(() => {
    // Read previous values BEFORE updating refs
    const wasLoadingModalOpen = prevModalOpen.current;
    const wasLoadingCancelled = prevIsCancelled.current;
    const isLoadingModalClosed = !remainingProductsModal.isOpen;
    const isLoadingNotCancelled = !remainingProductsModal.isCancelled;

    if (wasLoadingCancelled && wasLoadingModalOpen && isLoadingModalClosed) {
      // Clear pending options if cancelled (but keep popup open)
      if (isWaitingForProducts.current) {
        pendingPreviewOptions.current = null;
        isWaitingForProducts.current = false;
      }
    } else if (
      isWaitingForProducts.current &&
      wasLoadingModalOpen &&
      isLoadingModalClosed &&
      !wasLoadingCancelled &&
      isLoadingNotCancelled &&
      pendingPreviewOptions.current
    ) {
      const {
        date: pendingDate,
        stock: pendingStock,
        published: pendingPublished,
      } = pendingPreviewOptions.current;
      dispatch(setPreviewMode({ isPreviewMode: true }));
      // Set preview options with fully loaded products
      dispatch(
        setPreviewOptions({
          date: pendingDate
            ? formatDateForAnalytics(pendingDate.toDate())
            : null,
          stock: pendingStock === "hide",
          published: pendingPublished === "hide",
          products: products,
        }),
      );

      // Close the popup after loading completes
      setOpen(false);

      // Clear pending options and waiting flag
      pendingPreviewOptions.current = null;
      isWaitingForProducts.current = false;
    }

    // Update refs AFTER checking the transition
    prevModalOpen.current = remainingProductsModal.isOpen;
    prevIsCancelled.current = remainingProductsModal.isCancelled;
  }, [
    remainingProductsModal.isOpen,
    remainingProductsModal.isCancelled,
    products,
    dispatch,
    open,
  ]);

  useEffect(() => {
    setDate(previewOptions.date ? dayjs(previewOptions.date) : null);
    setStock(previewOptions.stock ? "hide" : "show");
    setPublished(previewOptions.published ? "hide" : "show");
  }, [previewOptions.date, previewOptions.stock, previewOptions.published]);

  const handleToggle = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setOpen((prev) => !prev);
  };

  const handleClose = (event) => {
    event?.stopPropagation();
    setOpen(false);
    setDate(previewOptions.date ? dayjs(previewOptions.date) : null);
    setStock(previewOptions.stock ? "hide" : "show");
    setPublished(previewOptions.published ? "hide" : "show");
  };

  const handleCancel = (event) => {
    event?.stopPropagation();
    setOpen(false);
    if (!isPreviewMode) {
      setDate(null);
      setStock("show");
      setPublished("show");
      dispatch(
        setPreviewOptions({
          date: null,
          stock: false,
          published: false,
        }),
      );
    } else {
      setDate(previewOptions.date ? dayjs(previewOptions.date) : null);
      setStock(previewOptions.stock ? "hide" : "show");
      setPublished(previewOptions.published ? "hide" : "show");
    }
  };

  const handlePreview = (event) => {
    event?.stopPropagation();

    let hasUnloadedProducts = false;

    // Check if all product details are loaded and trigger loading if needed
    if (isCategoryProductsV2Available && allProductIds.length > 0) {
      const pageSize = AppEnv.PRODUCT_LIST_PAGE_SIZE ?? 24;
      // Calculate first page product IDs
      const firstPageContentSlots =
        contentSlots?.filter((slot) => slot.position <= pageSize) || [];
      const firstPageContentSlotsCount = firstPageContentSlots.length;
      const pageProductIdsCount = pageSize - firstPageContentSlotsCount;
      const pageProductIds = allProductIds.slice(0, pageProductIdsCount);

      // Find only products that are NOT loaded
      const unloadedProductIds = allProductIds.filter(
        (productId) => !products[productId]?.productDetailsLoaded,
      );

      if (unloadedProductIds.length > 0) {
        hasUnloadedProducts = true;
        // Store preview options to apply after loading completes
        pendingPreviewOptions.current = {
          date,
          stock,
          published,
        };
        isWaitingForProducts.current = true;

        // Show modal immediately before starting to load
        dispatch(
          setRemainingProductsModal({
            isOpen: true,
            progress: 0,
            total: unloadedProductIds.length,
            loaded: 0,
          }),
        );

        // Load only unloaded products with modal
        dispatch(
          loadRemainingProducts({
            categoryId,
            catalogId,
            storeId,
            localeCode,
            allProductIds,
            pageProductIds,
          }),
        );
      }
    }

    // Only close popup and set preview options immediately if there are no unloaded products
    if (!hasUnloadedProducts) {
      setOpen(false);
      dispatch(setPreviewMode({ isPreviewMode: true }));
      dispatch(
        setPreviewOptions({
          date: date ? formatDateForAnalytics(date.toDate()) : null,
          stock: stock === "hide",
          published: published === "hide",
          products: products,
        }),
      );
    }
  };

  const triggerElement = cloneElement(children, {
    ref: anchorRef,
    onClick: (e: React.MouseEvent) => {
      // Call child's onClick first - it can prevent opening by calling preventDefault
      if (children.props.onClick) {
        const result = children.props.onClick(e);
        // If preventDefault was called or handler returned false, don't open
        if (e.defaultPrevented || result === false) {
          return; // Don't open popup
        }
      }
      // Only open if child's handler didn't prevent it
      handleToggle(e);
    },
  } as any);

  return (
    <Box>
      {triggerElement}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={
          remainingProductsModal.isOpen
            ? classes.popperWithModal
            : classes.popper
        }
      >
        <Paper
          elevation={4}
          style={classes.paper}
          onClick={(e) => e.stopPropagation()}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              sx={classes.datePicker}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
                popper: {
                  sx: classes.datePickerPopper,
                },
              }}
              label={intl.formatMessage({
                id: "previewPopup.date",
                defaultMessage: "Date",
              })}
              value={date}
              onChange={(newValue) => setDate(newValue)}
            />
          </LocalizationProvider>
          <Box style={classes.toggleDiv}>
            <Typography style={classes.description} variant="body1">
              {intl.formatMessage({
                id: "previewPopup.outOfStock",
                defaultMessage: "Out of Stock",
              })}
            </Typography>
            <OnOffToggle
              toggleType={stock}
              onToggleChange={(value) => setStock(value as "show" | "hide")}
              isFromPreviewPopup={true}
              labels={{
                on: { id: "previewPopup.hide", defaultMessage: "Hide" },
                off: { id: "previewPopup.show", defaultMessage: "Show" },
              }}
            />
          </Box>
          <Box style={classes.toggleDiv}>
            <Typography style={classes.description} variant="body1">
              {intl.formatMessage({
                id: "previewPopup.unpublished",
                defaultMessage: "Unpublished",
              })}
            </Typography>
            <OnOffToggle
              toggleType={published}
              onToggleChange={(value) => setPublished(value as "show" | "hide")}
              isFromPreviewPopup={true}
              labels={{
                on: { id: "previewPopup.hide", defaultMessage: "Hide" },
                off: { id: "previewPopup.show", defaultMessage: "Show" },
              }}
            />
          </Box>

          <Box style={classes.dailogAction}>
            <ButtonComponent
              color="secondaryButtonColorCTABlue"
              variant="outlined"
              onClick={handleCancel}
              padding="8px 0px"
            >
              {intl.formatMessage({
                id: "general.cancel",
                defaultMessage: "Cancel",
              })}
            </ButtonComponent>
            <ButtonComponent
              color="primary"
              variant="contained"
              onClick={handlePreview}
            >
              {intl.formatMessage({
                id: "productListSwitch.previewLabel",
                defaultMessage: "PREVIEW",
              })}
            </ButtonComponent>
          </Box>
        </Paper>
      </Popper>

      {/* Clicking anywhere else closes popup - but not when loading modal is open */}
      {open && !remainingProductsModal.isOpen && (
        <Box onClick={handleClose} style={classes.backdrop} />
      )}
    </Box>
  );
}
